

import { useSelector } from "react-redux";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
const hasPartnerWithDetails = (userContext = {}) => {
  const { hasPartner, partnerFirstName, partnerLastName } = userContext;
  return Boolean(hasPartner && (partnerFirstName || partnerLastName));
};

const SECTION_SEQUENCES = {
  basics: [
    "/basic/set-name",
    "/basic/marital-status",
    "/basic/dob",
    "/basic/location",
    "/basic/country",
    "/basic/dob",
    "/basic/gender",
    "/basic/children",
    "/basic/pets",
    "/setup/incapacity",
    "/basic/complete",
  ],
  family: [
    "/family/partners-name",
    "/family/partners-gender",
    "/family/partners-email",
    "/family/basics",
    "/family/pets",
    "/family/complete",
  ],
  guardian: [
    "/guardians/married-with-children",
    "/guardians/married-with-children/alternate",
    "/guardians/children",
    "/guardians/children/alternate",
    "/guardians/pets",
    "/guardians/pets/alternate",
    "/guardians/complete",
  ],
  distribution: [
    "/distribution/donations",
    "/distribution/specify-item",
    "/distribution/entitlement-age",
    "/distribution/remaining-assets",
    "/distribution/complete",
  ],
  executor: [
    "/executor/set-partner",
    "/executor/set-executor",
    "/executor/partner-co-executor",
    "/executor/alt-executor",
    "/executor/complete",
  ],
  poaAttorney: [
    "/poa/set-partner",
    "/poa/partner-co-poa",
    "/poa/set-poa",
    "/poa/alt-poa",
    "/poa/complete",
  ],
  poaCare: [
    "/poa/care/set-partner",
    "/poa/care/partner-co-poa",
    "/poa/care/set-poa",
    "/poa/care/alt-poa",
    "/poa/care/cpr-decision",
    "/poa/care/complete",
  ],
};

/**
 * The order in which questionnaire sections must be completed.
 * The stepper will always try to complete the first incomplete required section in this list.
 */
const SECTION_PRIORITY = [
  "basics",
  "family",
  "guardian",
  "distribution",
  "executor",
  "poaAttorney",
  "poaCare",
];

/**
 * Returns the starting route for a section based on user context.
 * This is "Context-Aware Routing" - e.g., single users skip partner names.
 *
 * @param {string} section - The section key (e.g., 'family')
 * @param {object} userContext - User flags like hasPartner, hasChildren, etc.
 * @returns {string} The path to the first step of that section.
 */
const getDefaultRouteForSection = (section, userContext) => {
  const partnerReady = hasPartnerWithDetails(userContext);
  const routes = {
    basics: "/basic/set-name",
    family: userContext?.hasPartner
      ? "/family/partners-name"
      : userContext?.hasChildren
        ? "/family/basics"
        : userContext?.hasPet
          ? "/family/pets"
          : "/family/basics",
    guardian: userContext?.hasChildren
      ? userContext?.hasPartner
        ? "/guardians/married-with-children"
        : "/guardians/children"
      : userContext?.hasPet
        ? "/guardians/pets"
        : "/distribution/progress",
    distribution: "/distribution/donations",
    executor: partnerReady
      ? "/executor/set-partner"
      : "/executor/set-executor",
    poaAttorney: partnerReady ? "/poa/set-partner" : "/poa/set-poa",
    poaCare: partnerReady
      ? "/poa/care/set-partner"
      : "/poa/care/set-poa",
  };
  return routes[section] || "/";
};

/**
 * Maps missing field names to their corresponding routes, scoped by section.
 */
const SECTION_FIELD_ROUTES = {
    basics: {
    firstName: "/basic/set-name",
    lastName: "/basic/set-name",
    middleName: "/basic/set-name",
    state: "/basic/location",
    country: "/basic/country",
    dateOfBirth: "/basic/dob",
    gender: "/basic/gender",
    maritalStatus: "/basic/marital-status",
    hasChildren: "/basic/children",
    hasPet: "/basic/pets",
    incapacityPlan: "/setup/incapacity",
  },
  family: {
    partnerFirstName: "/family/partners-name",
    partnerLastName: "/family/partners-name",
    partnerMiddleName: "/family/partners-name",
    partnerGender: "/family/partners-gender",
    partnerEmail: "/family/partners-email",
    children: "/family/basics",
    pets: "/family/pets",
  },
  guardian: {
    "childGuardian.fullname": "/guardians/children",
    "childGuardian.relationship": "/guardians/children",
    "alternateChildGuardian.fullname": "/guardians/children/alternate",
    "alternateChildGuardian.relationship": "/guardians/children/alternate",
    "petGuardian.fullname": "/guardians/pets",
    "petGuardian.relationship": "/guardians/pets",
    "alternatePetGuardian.fullname": "/guardians/pets/alternate",
    "alternatePetGuardian.relationship": "/guardians/pets/alternate",
    childGuardian: "/guardians/children",
    alternateChildGuardian: "/guardians/children/alternate",
    petGuardian: "/guardians/pets",
    alternatePetGuardian: "/guardians/pets/alternate",
  },
  distribution: {
    ageForInheritance: "/distribution/entitlement-age",
    charity: "/distribution/donations",
    giveOtherInheritors: "/distribution/remaining-assets",
    itemRecipient: "/distribution/specify-item",
  },
  executor: {
    "executor.putPartnerInCharge": "/executor/set-partner",
    "executor.fullname": "/executor/set-executor",
    "executor.relationship": "/executor/set-executor",
    "coExecutor.fullname": "/executor/partner-co-executor",
    "coExecutor.relationship": "/executor/partner-co-executor",
    "alternateExecutor.fullname": "/executor/alt-executor",
    "alternateExecutor.relationship": "/executor/alt-executor",
  },
  poaAttorney: {
    "executor.fullname": "/poa/set-poa",
    "executor.relationship": "/poa/set-poa",
    "coExecutor.fullname": "/poa/partner-co-poa",
    "coExecutor.relationship": "/poa/partner-co-poa",
    "alternateExecutor.fullname": "/poa/alt-poa",
    "alternateExecutor.relationship": "/poa/alt-poa",
  },
  poaCare: {
    "executor.fullname": "/poa/care/set-poa",
    "executor.relationship": "/poa/care/set-poa",
    "coExecutor.fullname": "/poa/care/partner-co-poa",
    "coExecutor.relationship": "/poa/care/partner-co-poa",
    "alternateExecutor.fullname": "/poa/care/alt-poa",
    "alternateExecutor.relationship": "/poa/care/alt-poa",
  },
};

/**
 * Determines the target route for a missing field, using section-scoped lookup
 * to avoid ambiguity, then falling back to section defaults.
 */
const getRouteForField = (section, fieldName, userContext) => {
  if (section === "guardian") {
    const isMarriedWithChildren = userContext?.hasChildren && userContext?.hasPartner;

    if (fieldName.startsWith("childGuardian")) {
      return isMarriedWithChildren
        ? "/guardians/married-with-children"
        : "/guardians/children";
    }

    if (fieldName.startsWith("alternateChildGuardian")) {
      return isMarriedWithChildren
        ? "/guardians/married-with-children/alternate"
        : "/guardians/children/alternate";
    }
  }

  const sectionRoutes = SECTION_FIELD_ROUTES[section];
  return (
    sectionRoutes?.[fieldName] ||
    getDefaultRouteForSection(section, userContext) ||
    "/"
  );
};

const isSectionRequired = (section, status, requiredSections) => {
  if (section === "guardian") {
    return requiredSections.guardian === true;
  }

  return status?.isRequired || requiredSections[section];
};

/**
 * Static helper to get the initial intro route for a new user.
 */
export const getIntroRouteForUser = (userContext, isFullyComplete = false) => {
  if (isFullyComplete) return "/";

  if (userContext?.hasPartner) {
    return "/setup/partner-estate";
  }
  return "/setup/progress";
};

/**
 * Path prefixes used to determine if the user is currently "working" within a specific section.
 * Note: poaAttorney explicitly excludes /poa/care/ to avoid collision with poaCare.
 */
const SECTION_PATHS = {
  basics: ["/basic/", "/setup/progress", "/setup/incapacity", "/setup/completion"],
  family: ["/family/"],
  guardian: ["/guardians/"],
  distribution: ["/distribution/"],
  executor: ["/executor/"],
  poaAttorney: ["/poa/"],
  poaCare: ["/poa/care/", "/poacare/"],
};

/**
 * Checks if the given path belongs to a specific section.
 */
const isPathInSection = (path, section) => {
  const paths = SECTION_PATHS[section];
  if (!paths) return false;
  if (section === "poaAttorney") {
    return path.startsWith("/poa/") && !path.startsWith("/poa/care/");
  }
  return paths.some((p) => path.startsWith(p));
};

/**
 * Returns the progress/intro screen route for a given section.
 * This is shown before the user starts the actual section questions.
 * Returns undefined for sections without an intro page.
 */
const getSectionIntroRoute = (section) => {
  const progressRoutes = {
    basics: "/setup/progress",
    family: "/family/progress",
    guardian: "/guardians/progress",
    distribution: "/distribution/progress",
    executor: "/executor/progress",
    poaAttorney: "/poa/progress",
    poaCare: "/poa/care/progress",
  };
  return progressRoutes[section];
};

const useStepper = () => {
  const location = useLocation();
  const sectionStatus = useSelector(
    (state) => state.metadata?.sectionStatus ?? {},
  );
  const requiredSections = useSelector(
    (state) => state.metadata?.requiredSections ?? {},
  );
  const isFullyComplete = useSelector(
    (state) => state.metadata?.isFullyComplete ?? false,
  );
  const overallProgress = useSelector(
    (state) => state.metadata?.percentage ?? 0,
  );

  const userContext = useSelector((state) => state.metadata?.userContext ?? {});
  const partnerReady = useMemo(
    () => hasPartnerWithDetails(userContext),
    [userContext],
  );

  /**
   * The core calculated property of the hook.
   * Dynamically determines where the 'Save & Continue' button should lead.
   */
  const nextPath = useMemo(() => {
    if (isFullyComplete) {
      return "/setup/complete";
    }

    const currentPath = location.pathname;

    console.log("[useStepper] evaluating nextPath", {
      currentPath,
      requiredSections,
      sectionStatus,
      userContext,
      partnerReady,
      isFullyComplete,
    });

    for (const section of SECTION_PRIORITY) {
      const status = sectionStatus[section];
      const isRequired = isSectionRequired(section, status, requiredSections);

      console.log("[useStepper] section check", {
        section,
        currentPath,
        isRequired,
        status,
      });

      if (!isRequired || status?.isComplete) continue;

      const introRoute = getSectionIntroRoute(section);
      const isAtIntro = currentPath === introRoute;
      const isInsideSection = isPathInSection(currentPath, section);

      if (!isInsideSection && !isAtIntro) {
        if (introRoute) {
          console.log("[useStepper] redirecting to section intro", {
            section,
            currentPath,
            introRoute,
          });
          return introRoute;
        } else {
          console.log("[useStepper] redirecting to default section route", {
            section,
            currentPath,
            defaultRoute: getDefaultRouteForSection(section, userContext),
          });
          return getDefaultRouteForSection(section, userContext);
        }
      }


      if (isAtIntro) {
        console.log("[useStepper] moving from intro to first section step", {
          section,
          currentPath,
          nextPath: getDefaultRouteForSection(section, userContext),
        });
        return getDefaultRouteForSection(section, userContext);
      }

      if (isInsideSection) {
        const sequence = SECTION_SEQUENCES[section] || [];
        const currentIndex = sequence.indexOf(currentPath);

        if (status?.missingFields?.length > 0) {
          for (const missingFieldObj of status.missingFields) {
            const fieldName = missingFieldObj.field || missingFieldObj;
            const fieldRoute = getRouteForField(section, fieldName, userContext);
            const fieldRouteIndex = sequence.indexOf(fieldRoute);
            
            if (fieldRoute !== currentPath && fieldRouteIndex >= currentIndex) {
              return fieldRoute;
            }
          }
        }

        if (currentIndex !== -1 && currentIndex < sequence.length - 1) {
          for (let i = currentIndex + 1; i < sequence.length; i++) {
            const nextInSequence = sequence[i];

            // Skip context checks for alternate guardian pages - backend handles their availability
            const isAlternateGuardian = nextInSequence.includes("alternate") && section === "guardian";

            if (
              !isAlternateGuardian &&
              (nextInSequence.includes("children") ||
                nextInSequence.includes("child")) &&
              !userContext?.hasChildren
            )
              continue;

            if (
              !isAlternateGuardian &&
               (nextInSequence.includes("partner") ||
                 nextInSequence.includes("partners")) &&
               !partnerReady
            ) {
              continue;
            }

            if (
              !isAlternateGuardian &&
              (nextInSequence.includes("pets") ||
                nextInSequence.includes("pet")) &&
              !userContext?.hasPet
            )
              continue;

            console.log("[useStepper] next in sequence selected", {
              section,
              currentPath,
              nextInSequence,
            });
            return nextInSequence;
          }
        }
      }

      if (status?.missingFields?.length > 0) {
        const firstMissingField = status.missingFields[0];
        const fieldName = firstMissingField.field || firstMissingField;
        const targetRoute = getRouteForField(section, fieldName, userContext);

        console.log("[useStepper] first missing field route selected", {
          section,
          currentPath,
          fieldName,
          targetRoute,
          missingFields: status.missingFields,
        });

        return targetRoute;
      }

       const completeRoute = `/${section === "poaAttorney" ? "poa" : section === "poaCare" ? "poa/care" : section}/complete`;
      if (isInsideSection && currentPath !== completeRoute) {
        console.log("[useStepper] completion route selected", {
          section,
          currentPath,
          completeRoute,
        });
        if (section === "basics") return "/basic/complete";
        return completeRoute;
      }

      console.log("[useStepper] falling back to default route", {
        section,
        currentPath,
        defaultRoute: getDefaultRouteForSection(section, userContext),
      });
      return getDefaultRouteForSection(section, userContext);
    }

    return "/";
  }, [
    sectionStatus,
    requiredSections,
    isFullyComplete,
    userContext,
    location.pathname,
  ]);

  /**
   * Used to determine the 'Next' path when a user specifically finishes a SECTION.
   * Useful for jumping between sections rather than individual steps.
   */
  const getNextPathAfterSection = (currentSection) => {
    if (isFullyComplete) {
      return "/setup/complete";
    }

    const currentIndex = SECTION_PRIORITY.indexOf(currentSection);

    const currentStatus = sectionStatus[currentSection];
    if (
      currentStatus &&
      !currentStatus.isComplete &&
      currentStatus.missingFields?.length > 0
    ) {
      const firstMissingField = currentStatus.missingFields[0];
      const fieldName = firstMissingField.field || firstMissingField;
      return getRouteForField(currentSection, fieldName, userContext);
    }

    for (let i = currentIndex + 1; i < SECTION_PRIORITY.length; i++) {
      const section = SECTION_PRIORITY[i];
      const status = sectionStatus[section];
      const isRequired = isSectionRequired(section, status, requiredSections);

      if (status && isRequired && !status.isComplete) {
        const introRoute = getSectionIntroRoute(section);
  
        return introRoute || getDefaultRouteForSection(section, userContext);
      }
    }

    return "/summary";
  };

  return {
    nextPath, 
    isFullyComplete, 
    overallProgress, 
    sectionStatus, 
    requiredSections, 
    getNextPathAfterSection, 
    SECTION_PRIORITY, 
  };
};

export default useStepper;
